import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import {
  Button,
  ButtonIcon,
  DateAndTimePicker,
  FooterActionBar,
  Icon,
  InlineMessage,
  MobileDrawer,
  ModalIsland,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import { getPredefinedComments } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_USERMANAGER,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox/CertsModalCommentBox';
import { PREDEFINED_COMMENTS_FOR_ACTION } from '../constant';

import invertedMessages from './messages';

import './CertsChangeEndDate.css';

function CertsChangeEndDate({
  handleClick,
  handleCancel,
  getCurrentEndDate,
  isChangeEndDateError,
  setIsChangeEndDateError,
  allowedChangeExtendDate,
  isLoading,
  certificationStartDate,
  errorMessage,
  getDefaultEndDate,
  isCertsKindUser,
  isMobileXlargeWidth,
}) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);
  const [isCritical, setIsCritical] = useState(false);

  const { predefinedComments, campaignType } = useSelector(
    (state) => state?.certifications?.details?.core
  );
  const { messages } = useSelector((state) => state?.user);
  const { isActionLoading } = useSelector(
    (state) => state?.certifications?.details?.lineActions
  );

  const isUMCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_USERMANAGER;

  let checkMinDate = moment(new Date()).isAfter(
    moment(certificationStartDate),
    'day'
  );

  checkMinDate = checkMinDate ? new Date() : certificationStartDate;
  const defaultDate = getDefaultEndDate || null;

  const [startDate, setStartDate] = useState(defaultDate);
  const [isDateCritical, setIsDateCritical] = useState(false);
  const [getAllowedChangeExtendDates, setAllowedChangeExtendDates] =
    useState(checkMinDate);
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const [commentsDetails, setCommentDetails] = useState({
    commentDescription: '',
    predefComments: [],
    isCommentApplied: false,
    showAlert: false,
  });

  const isCommentValid = Boolean(
    commentsDetails?.commentDescription?.trim().length >= 3 &&
      !/<|>/.test(commentsDetails?.commentDescription)
  );
  const isFormValid = isCommentValid && defaultDate !== startDate;

  useEffect(() => {
    const lineItemType = isCertsKindUser
      ? PREDEFINED_COMMENTS_FOR_ACTION.EXTEND_END_DATE_USERS
      : PREDEFINED_COMMENTS_FOR_ACTION.EXTENDENDDATEFORACCESS;

    if (!(lineItemType in predefinedComments)) {
      dispatch(getPredefinedComments(lineItemType)).catch((error) =>
        Logger.log(error)
      );
    }
  }, [isCertsKindUser]);

  useEffect(() => {
    const lineItemType = isCertsKindUser
      ? PREDEFINED_COMMENTS_FOR_ACTION.EXTEND_END_DATE_USERS
      : PREDEFINED_COMMENTS_FOR_ACTION.EXTENDENDDATEFORACCESS;

    if (lineItemType in predefinedComments) {
      setCommentDetails((prevState) => ({
        ...prevState,
        predefComments: predefinedComments[lineItemType] || [],
      }));
    }
  }, [predefinedComments, isCertsKindUser]);

  useEffect(() => {
    let getAllowedDays;

    if (Number(allowedChangeExtendDate) > 1) {
      getAllowedDays = Number(allowedChangeExtendDate) - 1;
    } else {
      getAllowedDays = 0;
    }

    if (allowedChangeExtendDate && allowedChangeExtendDate.length > 0) {
      const minDateForDatePicker = moment(certificationStartDate).add(
        getAllowedDays,
        'days'
      );

      setAllowedChangeExtendDates(minDateForDatePicker.toDate());
    } else {
      setAllowedChangeExtendDates(null);
    }
  }, []);

  useEffect(() => {
    if (startDate) {
      setIsDateCritical(false);
    }
  }, [startDate]);

  const handleSave = () => {
    if (!startDate) {
      setIsDateCritical(true);
      setCommentDetails({
        ...commentsDetails,
        showAlert: true,
      });
    }

    if (!isCommentValid) {
      setIsCritical(true);
      setCommentDetails({
        ...commentsDetails,
        showAlert: true,
      });
    }

    if (startDate && isCommentValid) {
      let currentDate = startDate;
      const dd = currentDate.getDate();
      const mm = currentDate.getMonth() + 1;
      const yyyy = currentDate.getFullYear();

      currentDate = `${mm < 10 ? `0${mm}` : mm}-${
        dd < 10 ? `0${dd}` : dd
      }-${yyyy}`;
      let comments = '';

      if (commentsDetails?.commentDescription) {
        comments = commentsDetails.commentDescription;
      }

      handleClick(currentDate, comments);
    }
  };

  const handleCommentText = (text) => {
    if (text && isCritical) {
      setIsCritical(false);
    }

    setCommentDetails({
      ...commentsDetails,
      commentDescription: text,
      showAlert: text.length === 0 ? commentsDetails.showAlert : false,
    });
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsChangeEndDateError();
    handleCancel();
  };

  const getChangeEndDateModal = () => {
    const changeEndDateActionBodyContent = () => (
      <>
        <StackingAlertBanner
          colorTheme='Critical'
          title='Error'
          description={errorMessage}
          isVisible={isChangeEndDateError}
          onCancel={setIsChangeEndDateError}
        />
        <div className='CertsChangeEndDate-CommonMargin-Container'>
          {isUMCampaign && isCertsKindUser && (
            <>
              <Typography kind='h3'>
                {messages.CERTS_CHANGE_END_DATE ||
                  localize(intl, getMessageKey('CERTS_CHANGE_END_DATE'))}
              </Typography>
              <Typography
                variant='body3'
                className={classnames(
                  `CertsChangeEndDate-Title${
                    getCurrentEndDate && getCurrentEndDate === '-'
                      ? ' CertsChangeEndDate-title--isEmpty'
                      : ''
                  }`
                )}>
                {getCurrentEndDate}
              </Typography>
            </>
          )}
        </div>
        <DateAndTimePicker
          isDisabled={allowedChangeExtendDate === '0'}
          classNames={{
            calenderClassName: 'CertsChangeEndDate-CommonMargin-Container',
          }}
          kind='dateStart'
          label={
            messages.CERTS_NEW_END_DATE ||
            localize(intl, getMessageKey('CERTS_NEW_END_DATE'))
          }
          isRequired
          isCritical={isDateCritical}
          setIsCritical={setIsDateCritical}
          CriticalHelperText={
            <InlineMessage
              text={
                messages.CERTS_SELECT_NEW_DATE ||
                localize(intl, getMessageKey('CERTS_SELECT_NEW_DATE'))
              }
              colorTheme='critical'
              size='small'
              leftIcon={
                <Icon kind='AlertCriticalFilled' color='critical-700' />
              }
            />
          }
          startDate={startDate}
          setStartDate={setStartDate}
          minDate={checkMinDate}
          maxDate={getAllowedChangeExtendDates}
        />
        <CertsModalCommentBox
          defaultComments={commentsDetails?.predefComments}
          showAlert={isCritical}
          onChange={handleCommentText}
          label={
            messages['comments.ATTESTATIONS'] ||
            localize(intl, getMessageKey('comments.ATTESTATIONS'))
          }
          dataTestId='CertsChangeEndDate-commentBoxTestId'
          allowEditPredefinedComments={
            predefinedComments?.allowEditPredefinedComments
          }
        />
      </>
    );
    const changeEndDateActionFooterContent = () => (
      <>
        <Button
          type='button'
          kind='ghost'
          isDisabled={isLoading}
          size='medium'
          onClick={() => {
            setIsOpen(false);
            handleCancel();
          }}>
          {messages.CERTS_CANCEL ||
            localize(intl, getMessageKey('CERTS_CANCEL'))}
        </Button>
        <Button
          className='CertsChangeEndDate-saveButton'
          type='button'
          kind={isFormValid ? 'filled' : 'outlined'}
          isLoading={isActionLoading}
          isDisabled={isLoading}
          size='medium'
          onClick={() => handleSave()}>
          {messages['SaveLabel.ATTESTATIONS'] ||
            localize(intl, getMessageKey('SaveLabel.ATTESTATIONS'))}
        </Button>
      </>
    );

    return !isMobileXlargeWidth ? (
      <ModalIsland
        onClose={() => handleClose()}
        className='CertsChangeEndDateModal-wrapper'
        classNames={{
          sectionClassName: 'CertsChangeEndDateModal-wrapper--section',
          bodyClassName: 'CertsChangeEndDateModal-wrapper--body',
        }}
        isOpen={isOpen}
        headerIcon={
          <Icon kind='ExtendEndDate' color='neutral-1000' size='large' />
        }
        title={
          <div className='CertsChangeEndDateModal-titleWrapper'>
            <div className='CertsChangeEndDateModal-commentsTitle'>
              {messages.CERTS_CHANGE_END_DATE ||
                localize(intl, getMessageKey('CERTS_CHANGE_END_DATE'))}
            </div>
          </div>
        }
        subtitle={
          <div className='CertsChangeEndDateModal-commentsDesc'>
            {messages.CERTS_CHANGE_END_DATE_MESSAGE ||
              localize(intl, getMessageKey('CERTS_CHANGE_END_DATE_MESSAGE'))}
          </div>
        }
        contentBody={changeEndDateActionBodyContent()}
        FooterActionBarComp={
          <FooterActionBar
            size='large'
            dataTestId='CertsChangeEndDate-footerTestId'>
            <div />
            <div className='CertsChangeEndDate-buttonWrapper'>
              {changeEndDateActionFooterContent()}
            </div>
          </FooterActionBar>
        }
      />
    ) : (
      <MobileDrawer
        headerContent={
          <header className='CertsChangeEndDate-mobileDrawer-header'>
            <div className='CertsChangeEndDate-mobileDrawer-titleContainer'>
              <Icon kind='ExtendEndDate' color='neutral-1000' size='large' />
              <div className='CertsChangeEndDateModal-titleWrapper'>
                <Typography
                  kind='h1'
                  className='CertsChangeEndDateModal-commentsTitle'>
                  {messages.CERTS_CHANGE_END_DATE ||
                    localize(intl, getMessageKey('CERTS_CHANGE_END_DATE'))}
                </Typography>
                <Typography
                  kind='body2'
                  className='CertsChangeEndDateModal-commentsDesc'>
                  {messages.CERTS_CHANGE_END_DATE_MESSAGE ||
                    localize(
                      intl,
                      getMessageKey('CERTS_CHANGE_END_DATE_MESSAGE')
                    )}
                </Typography>
              </div>
            </div>
            <ButtonIcon
              size='medium'
              kind='ghost'
              ariaLabel={l('Close')}
              icon={
                <Icon kind='close' color='neutral-100' size='smallMedium' />
              }
              onClick={() => handleClose()}
            />
          </header>
        }
        content={changeEndDateActionBodyContent()}
        footerActionBar={
          <FooterActionBar size='large' className='CertsActionPopover-controls'>
            {changeEndDateActionFooterContent()}
          </FooterActionBar>
        }
        className='CertsChangeEndDate-mobileDrawer'
        classNames={{
          headerClassName: 'CertsChangeEndDate-mobileDrawer--headerWrapper',
          contentClassName: 'CertsChangeEndDate-mobileDrawer--content',
        }}
        setIsOpen={() => handleClose()}
        isOpen={isOpen}
      />
    );
  };

  return (
    <section className='proof-that-this-element-is-rendered-in-test-file-while-rendering'>
      {getChangeEndDateModal()}
    </section>
  );
}

CertsChangeEndDate.propTypes = {
  handleClick: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  getCurrentEndDate: PropTypes.string.isRequired,
  isChangeEndDateError: PropTypes.bool.isRequired,
  setIsChangeEndDateError: PropTypes.func.isRequired,
  allowedChangeExtendDate: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  certificationStartDate: PropTypes.instanceOf(Date).isRequired,
  errorMessage: PropTypes.string,
  getDefaultEndDate: PropTypes.string.isRequired,
  isCertsKindUser: PropTypes.bool.isRequired,
  isMobileXlargeWidth: PropTypes.bool.isRequired,
};

CertsChangeEndDate.defaultProps = {
  errorMessage: 'Error! Please try again later.',
  isLoading: null,
};

export default CertsChangeEndDate;
